/**
 * PairInviteAcceptPage — S02 pair invite accept (Phase I).
 *
 * Route: /pair/invite/:token
 *
 * Reachable without sign-in until the accept call is made. The page:
 *   1. Reads the token from the URL path.
 *   2. Strips the token from the URL via history.replaceState (R11).
 *   3. If the user is not signed in, shows a "sign in to accept" prompt.
 *   4. Once signed in, calls acceptPairInvite(token) and redirects on success.
 *
 * Security (CISO requirements):
 *   R7:  Never fetches token contents pre-auth — shows only "sign in to accept".
 *   R11: Sets Referrer-Policy: no-referrer via <meta> and strips token from URL.
 *   R19: Accept call goes through the AppSync Lambda data source, not a direct
 *        DDB resolver.
 */
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { appsyncQuery } from '../api/appsync-client.js'
import { Button, PageContainer } from '../ui'

// ─── GraphQL ──────────────────────────────────────────────────────────────────

const ACCEPT_PAIR_INVITE = `
  mutation AcceptPairInvite($token: String!) {
    acceptPairInvite(token: $token) {
      pair {
        pairId
        reportId
        managerId
        status
        cadence
        reportName
        reportEmail
        managerName
        managerEmail
      }
    }
  }
`

// ─── PairInviteAcceptPage ─────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object|null} props.me - The current user from AuthProvider (null if unauthenticated).
 * @param {string|null} props.loginUrl - URL to redirect to for sign-in.
 */
export function PairInviteAcceptPage({ me = null, loginUrl = '/login' }) {
  const { token } = useParams()
  const navigate = useNavigate()
  const tokenRef = useRef(token)
  const [status, setStatus] = useState('idle') // idle | accepting | success | error
  const [errorMessage, setErrorMessage] = useState(null)
  const [accepted, setAccepted] = useState(null) // pair data on success

  // R11: Strip the token from the URL immediately after reading it into state.
  // This prevents the token from appearing in Referer headers sent to any
  // third-party resources loaded on this page.
  useEffect(() => {
    if (token) {
      window.history.replaceState({}, '', '/pair/invite/accepted')
    }
  }, [token])

  // Auto-accept once the user is signed in and token is available
  useEffect(() => {
    if (!me || !tokenRef.current || status !== 'idle') return
    acceptInvite(tokenRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])

  async function acceptInvite(rawToken) {
    setStatus('accepting')
    setErrorMessage(null)
    try {
      const data = await appsyncQuery(ACCEPT_PAIR_INVITE, { token: rawToken })
      setAccepted(data?.acceptPairInvite?.pair ?? null)
      setStatus('success')
    } catch (err) {
      setStatus('error')
      const code = err?.code
      if (code === 'INVITE_INVALID') {
        setErrorMessage('This invite link is not valid or has expired.')
      } else if (code === 'PAIR_EXISTS') {
        setErrorMessage('You already have an active pair with this manager.')
      } else if (code === 'SELF_INVITE') {
        setErrorMessage('You cannot accept your own invite.')
      } else if (code === 'EMAIL_NOT_VERIFIED') {
        setErrorMessage('Your email address must be verified before accepting an invite.')
      } else {
        setErrorMessage('Something went wrong. Please try again or ask for a new invite link.')
      }
    }
  }

  // ── Render states ──────────────────────────────────────────────────────────

  // R7: pre-auth — show only a generic prompt, never token contents
  if (!me) {
    return (
      <PageContainer as="main" className="invite-accept">
        {/* R11: no-referrer policy */}
        <meta name="referrer" content="no-referrer" />
        <div className="invite-accept__card" role="region" aria-label="Pair invite">
          <h1 className="invite-accept__title">You have been invited to pair</h1>
          <p className="invite-accept__body">
            Sign in to accept this invite and start working with your pair partner.
          </p>
          <a
            className="btn btn--primary btn--md"
            href={`${loginUrl}?redirect=/pair/invite/${token}`}
          >
            Sign in to accept
          </a>
        </div>
      </PageContainer>
    )
  }

  if (status === 'accepting') {
    return (
      <PageContainer as="main" className="invite-accept">
        <div
          className="invite-accept__card"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <p className="invite-accept__body">Accepting your invite…</p>
        </div>
      </PageContainer>
    )
  }

  if (status === 'success' && accepted) {
    const partnerName =
      accepted.managerName || accepted.managerEmail || accepted.managerId
    return (
      <PageContainer as="main" className="invite-accept">
        <div
          className="invite-accept__card invite-accept__card--success"
          role="status"
          aria-live="polite"
        >
          <h1 className="invite-accept__title">Pair accepted</h1>
          <p className="invite-accept__body">
            You are now paired with <strong>{partnerName}</strong>.
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(`/pair/${accepted.pairId}`)}
            aria-label={`Go to your pair with ${partnerName}`}
          >
            Go to pair
          </Button>
        </div>
      </PageContainer>
    )
  }

  if (status === 'error') {
    return (
      <PageContainer as="main" className="invite-accept">
        <div
          className="invite-accept__card invite-accept__card--error"
          role="alert"
        >
          <h1 className="invite-accept__title">Could not accept invite</h1>
          <p className="invite-accept__body">{errorMessage}</p>
          <Button
            variant="ghost"
            onClick={() => navigate('/home')}
            aria-label="Go to dashboard"
          >
            Go to dashboard
          </Button>
        </div>
      </PageContainer>
    )
  }

  // Idle with me present — acceptInvite will fire via the useEffect above
  return (
    <PageContainer as="main" className="invite-accept">
      <div className="invite-accept__card" role="status" aria-busy="true" aria-live="polite">
        <p className="invite-accept__body">Preparing to accept your invite…</p>
      </div>
    </PageContainer>
  )
}
